import { Injectable } from '@angular/core';
// import removed
import { SwapProvider } from './providers/swap-provider.interface';
import { ZeroExProvider } from './providers/zero-ex.provider';
import { DexorNativeProvider } from './providers/dexor-native.provider';
import { Web3Service } from './web3.service';
import { environment } from '../../environments/environment';

export interface RouteQuote {
  type: 'DEX' | 'AGGREGATOR' | 'EXTERNAL_NATIVE' | 'WRAP' | 'UNWRAP';
  platformName: string;
  targetAddress?: string;
  allowanceTarget?: string;
  callData?: string;
  value?: string;
  estimatedOutput?: bigint;
  estimatedInput?: bigint;
  gasEstimate?: string;
  priceImpact?: string;
  permit2?: any;
}

import { AppStateService } from './app-state.service';
import { inject, Injector } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class RoutingService {
  private nativeProvider: SwapProvider;
  private injector = inject(Injector);

  constructor(private web3Service: Web3Service) {
    this.nativeProvider = new DexorNativeProvider(this.web3Service);
  }

  private isInternalToken(address: string): boolean {
    const addr = address.toLowerCase();
    const contracts = this.web3Service.getContractsConfig();
    return (
      addr === contracts.TOKENS.DXOR.toLowerCase() ||
      addr === contracts.TOKENS.TOKEN_A.toLowerCase() ||
      addr === contracts.TOKENS.TOKEN_B.toLowerCase()
    );
  }

  /**
   * Evaluates the swap and determines the best platform.
   * If the pair involves an internal token, it routes to DEX natively.
   * Otherwise, it simulates fetching the best price from an Aggregator API (like 0x or 1inch)
   * and routes to Uniswap V3.
   */
  async getBestSwapRoute(
    tokenInAddress: string,
    tokenOutAddress: string,
    amount: bigint,
    isExactOut: boolean = false,
    isExternal: boolean = false,
    feeTiers?: number[],
  ): Promise<RouteQuote> {
    const NATIVE = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';
    const WRAPPED_TOKENS = [
      '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2', // WETH
      '0xfff9976782d46cc05630d1f6ebab18b2324d6b14', // Sepolia WETH
      '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c', // WBNB
      '0xae13d989dac2f0debff460ac112a837c89baa7cd', // BSC Testnet WBNB
      '0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270', // WMATIC / WPOL
    ];
    
    if (tokenInAddress.toLowerCase() === NATIVE && WRAPPED_TOKENS.includes(tokenOutAddress.toLowerCase())) {
      return {
        type: 'WRAP',
        platformName: 'Native Wrapping',
        estimatedOutput: amount,
        estimatedInput: amount,
      } as RouteQuote;
    }
    
    if (WRAPPED_TOKENS.includes(tokenInAddress.toLowerCase()) && tokenOutAddress.toLowerCase() === NATIVE) {
      return {
        type: 'UNWRAP',
        platformName: 'Native Unwrapping',
        estimatedOutput: amount,
        estimatedInput: amount,
      } as RouteQuote;
    }

    // Check if it's a platform-specific pair
    if (!isExternal) {
      console.log(
        'Routing Service: Native DEX Pair detected. Fetching live quote from QuoterV2.',
      );
      const quote = await this.nativeProvider.getQuote(tokenInAddress, tokenOutAddress, amount, isExactOut, feeTiers);

      // Simulate live market fluctuations for internal testnet pairs so it feels alive like Uniswap
      if (quote.estimatedOutput && !isExactOut) {
        const fluctuation = (Math.random() - 0.5) * 0.002; // max +/- 0.1% change
        const currentOut = Number(quote.estimatedOutput);
        quote.estimatedOutput = BigInt(Math.floor(currentOut * (1 + fluctuation)));
      } else if (quote.estimatedInput && isExactOut) {
        const fluctuation = (Math.random() - 0.5) * 0.002;
        const currentIn = Number(quote.estimatedInput);
        quote.estimatedInput = BigInt(Math.floor(currentIn * (1 + fluctuation)));
      }

      return quote;
    }
    // Forward the request to the active SwapProvider
    console.log(
      'Routing Service: Non-native Pair detected. Fetching route from external SwapProvider...',
    );
    const appStateService = this.injector.get(AppStateService);
    const takerAddress = this.web3Service.address() || '0x6CcAebE6a435D7C01AFD18d72b75568b3f2da8C7';
    const chainId = this.web3Service.chainId() || 1;

    let externalNetwork = 'ETH';
    if (chainId === 56 || chainId === 97) externalNetwork = 'BSC';
    if (chainId === 137 || chainId === 80002) externalNetwork = 'POLYGON';

    const dynamicSwapProvider = new ZeroExProvider(externalNetwork, takerAddress, chainId);
    return dynamicSwapProvider.getQuote(tokenInAddress, tokenOutAddress, amount, isExactOut, feeTiers);
  }

  /**
   * Evaluates the liquidity provision and determines the best yield platform.
   */
  async getBestLiquidityRoute(
    token0Address: string,
    token1Address: string,
    amount0: bigint,
    amount1: bigint,
    isExternal: boolean = false,
  ): Promise<RouteQuote> {
    if (!isExternal) {
      console.log('Routing Service: Native DEX Pair detected. Routing to DEX PositionManager.');
      return {
        type: 'DEX',
        platformName: 'DEX Native Liquidity',
      };
    }

    console.log('Routing Service: Popular Pair detected. Routing to external Position Manager...');
    await new Promise((resolve) => setTimeout(resolve, 800));

    return {
      type: 'EXTERNAL_NATIVE',
      platformName: 'Uniswap V3 (Native Route)',
      targetAddress: '0x1238536071E1c677A632429e3655c799b22cDA52', // Sepolia Uniswap V3 PositionManager
    };
  }
}
